import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
    TableState,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    useReactTable,
    Row,
    createColumnHelper,
    getExpandedRowModel
} from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleDown } from "@fortawesome/free-solid-svg-icons/faChevronCircleDown";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { FormattedAlertEventHistory } from "@idw-types/data/alert-event-history/alert-event-history";
import { formatDateToString, sortDates } from "@utils/date-util";
import { sortStrings } from "@utils/string-util";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { AlertEventIncidentsTableContainer } from "../alert-event-incidents-table-container/alert-event-incidents-table-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { useTableFilters } from "@hooks/use-table-filters";
import styles from "./alert-events-table-container.less";
import {
    handleSetColumnFiltersChange,
    handleSetPaginationChange,
    handleSetSortingChange,
    handleSetExpandedChange
} from "@utils/react-table/react-table-util";

type AlertEventsTableContainerProps = {
    alertEvents: Array<FormattedAlertEventHistory>;
    tableState: TableState;
    isPageHeight: boolean;
    handleClickOnTableCellLink: (alertEvent: FormattedAlertEventHistory) => void;
};

type AlertEventsTableRowData = FormattedAlertEventHistory & {
    alertName: string;
    companyName: string;
    workflowName: string;
    rpServiceName: string;
    credentials: string;
    subRows: Array<any>;
};

export const AlertEventsTableContainer = (props: AlertEventsTableContainerProps) => {
    const { alertEvents, tableState, isPageHeight, handleClickOnTableCellLink } = props;
    const dispatch = useDispatch();

    const alertEventsWithSubrows: Array<AlertEventsTableRowData> = useMemo(() => alertEvents.map(alertEvent => {
        return {
            ...alertEvent,
            alertName: alertEvent.alert?.name || resources.common.notFound,
            companyName: alertEvent.company?.name || resources.common.notFound,
            workflowName: alertEvent.cause?.workflow?.name || resources.common.notFound,
            rpServiceName: alertEvent.cause?.rpService?.serviceName || resources.common.notFound,
            credentials: alertEvent.cause?.credentials?.join(", ") || resources.common.notFound,
            subRows: [{}]
        }
    }), [alertEvents]);

    const columnHelper = createColumnHelper<AlertEventsTableRowData>();
    const columns = useMemo(() => ([
        columnHelper.display({
            id: "expander",
            enableSorting: false,
            cell: (props) => {
                const { row } = props;
                return (
                    <button className={styles.chevronButton} id={`alert-event-expander-${row.index}`} onClick={row.getToggleExpandedHandler()}>
                        <FontAwesomeIcon className={`${styles.chevron} ${row.getIsExpanded() && styles.rotate}`} icon={faChevronCircleDown} />
                    </button>
                );
            }
        }),
        columnHelper.accessor("dateTriggered", {
            header: resources.tables.alertEvents.columns.dateTriggered.header,
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null,
            sortingFn: (a, b) => sortDates(a.original.dateTriggered, b.original.dateTriggered),
            filterFn: "dateTimeFilter"
        }),
        columnHelper.accessor("alertName", {
            header: resources.tables.alertEvents.columns.alert.header,
            sortingFn: (a, b) => sortStrings(a.original.alertName, b.original.alertName),
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("companyName", {
            header: resources.tables.alertEvents.columns.organization.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("workflowName", {
            header: resources.tables.alertEvents.columns.workflow.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("rpServiceName", {
            header: resources.tables.alertEvents.columns.service.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("credentials", {
            header: resources.tables.alertEvents.columns.credential.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.display({
            id: "details",
            header: resources.tables.alertEvents.columns.details.header,
            cell: (props) => (
                <a className={styles.link} onClick={() => handleClickOnTableCellLink(props.row.original)}>
                    {resources.tables.alertEvents.columns.details.content}
                </a>
            ),
            enableSorting: false
        })
    ]), [alertEventsWithSubrows]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: alertEventsWithSubrows,
        state: tableState,
        autoResetPageIndex: true,
        filterFns: filterTypes,
        paginateExpandedRows: false,
        sortDescFirst: false,
        onExpandedChange: (sortingOrUpdateFn) => handleSetExpandedChange(sortingOrUpdateFn, TableSettingsTableTypes.ALERT_EVENTS, tableState.expanded, dispatch),
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.ALERT_EVENTS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.ALERT_EVENTS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.ALERT_EVENTS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getRowCanExpand: () => true,
        getExpandedRowModel: getExpandedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    const renderExpandedComponent = useCallback((row: Row<FormattedAlertEventHistory>) => {
        return (
            <div className={styles.expandedTableRowContainer}>
                <div className={styles.expandableTableHeader}>{resources.tables.alertEventIncidents.title}</div>
                <AlertEventIncidentsTableContainer dateTriggered={row.original.dateTriggered} cause={row.original.cause} />
            </div>
        );
    }, []);

    return (
        <IdwTableContainer className={styles.alertEventsTableContainer}>
            <IdwTable
                id="alert-events"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"alert-events"}
                renderExpandedComponent={renderExpandedComponent}
                className={styles.alertEventsTable}
            />
            <PaginationContainer
                idPrefix="alert-events"
                isPageHeight={isPageHeight}
                useTableResult={useTableResult}
            />
        </IdwTableContainer>
    );
};